import { useEffect } from "react";
import { ExternalLink, Landmark, BarChart3, Scale, DollarSign, MapPin, ClipboardCheck, HeartHandshake } from "lucide-react";

const HELPING_HAND_IMG = "https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?crop=entropy&cs=srgb&fm=jpg&q=85";

const GROUPS = [
  {
    id: "federal",
    title: "Federal Public Health Agencies",
    icon: Landmark,
    blurb: "Primary U.S. agencies setting public health policy, guidance, and funding priorities.",
    links: [
      { name: "Centers for Disease Control and Prevention (CDC)", url: "https://www.cdc.gov", desc: "National public health agency — data, guidance, and programs." },
      { name: "Health Resources & Services Administration (HRSA)", url: "https://www.hrsa.gov", desc: "Access to care for underserved and rural communities." },
      { name: "Substance Abuse and Mental Health Services Administration (SAMHSA)", url: "https://www.samhsa.gov", desc: "Behavioral health, mental health, and substance use." },
      { name: "National Institutes of Health (NIH)", url: "https://www.nih.gov", desc: "Biomedical and public health research." },
      { name: "Centers for Medicare & Medicaid Services (CMS)", url: "https://www.cms.gov", desc: "Medicare, Medicaid, and health coverage programs." },
      { name: "Agency for Healthcare Research & Quality (AHRQ)", url: "https://www.ahrq.gov", desc: "Evidence to improve healthcare quality and safety." },
      { name: "HHS Office of the Assistant Secretary for Health (OASH)", url: "https://www.hhs.gov/ash", desc: "Population health and prevention leadership." },
      { name: "SAMHSA — LGBTQI+ Resources", url: "https://www.samhsa.gov/behavioral-health-equity/lgbtqi", desc: "Federal behavioral health resources and support for LGBTQI+ individuals and communities." },
      { name: "National Domestic Violence Hotline", url: "https://www.thehotline.org", desc: "Confidential 24/7 support for all survivors of domestic abuse — men and women, straight and LGBTQ. Call 1-800-799-7233 or text START to 88788." },
    ],
  },
  {
    id: "equity",
    title: "Health Equity & Social Determinants",
    icon: Scale,
    blurb: "Resources advancing racial, educational, and health-system equity.",
    links: [
      { name: "HHS Office of Minority Health (OMH)", url: "https://www.minorityhealth.hhs.gov", desc: "Improving health for racial and ethnic minority populations." },
      { name: "National Institute on Minority Health & Health Disparities (NIMHD)", url: "https://www.nimhd.nih.gov", desc: "Research to reduce health disparities." },
      { name: "CDC Office of Health Equity", url: "https://www.cdc.gov/health-equity", desc: "Strategies and data on health equity." },
      { name: "CDC Social Vulnerability Index (SVI)", url: "https://www.atsdr.cdc.gov/place-health/php/svi/index.html", desc: "Identify communities most at risk during emergencies." },
      { name: "Robert Wood Johnson Foundation (RWJF)", url: "https://www.rwjf.org", desc: "Building a national culture of health and equity." },
      { name: "KFF (Kaiser Family Foundation)", url: "https://www.kff.org", desc: "Health policy analysis, polling, and disparities data." },
    ],
  },
  {
    id: "data",
    title: "Data, Surveillance & Population Health",
    icon: BarChart3,
    blurb: "Datasets and tools for community assessment and outcomes measurement.",
    links: [
      { name: "Healthy People 2030", url: "https://health.gov/healthypeople", desc: "National health objectives and evidence-based resources." },
      { name: "CDC WONDER", url: "https://wonder.cdc.gov", desc: "Public health data query system (mortality, natality, more)." },
      { name: "Behavioral Risk Factor Surveillance System (BRFSS)", url: "https://www.cdc.gov/brfss", desc: "State-based health behavior surveillance." },
      { name: "National Center for Health Statistics (NCHS)", url: "https://www.cdc.gov/nchs", desc: "Vital and health statistics for the nation." },
      { name: "County Health Rankings & Roadmaps", url: "https://www.countyhealthrankings.org", desc: "County-level health outcomes and factors." },
      { name: "U.S. Census Bureau — American Community Survey", url: "https://www.census.gov/programs-surveys/acs", desc: "Demographic and socioeconomic community data." },
    ],
  },
  {
    id: "funding",
    title: "Funding & Procurement",
    icon: DollarSign,
    blurb: "Where agencies and nonprofits find and manage federal funding.",
    links: [
      { name: "Grants.gov", url: "https://www.grants.gov", desc: "Find and apply for federal grant opportunities." },
      { name: "SAM.gov", url: "https://sam.gov", desc: "Federal entity registration and contract opportunities." },
      { name: "HRSA Grants", url: "https://www.hrsa.gov/grants", desc: "Health workforce and access grant programs." },
      { name: "SAMHSA Grants", url: "https://www.samhsa.gov/grants", desc: "Behavioral health funding opportunities." },
      { name: "USAspending.gov", url: "https://www.usaspending.gov", desc: "Track federal spending and awards." },
    ],
  },
  {
    id: "nevada",
    title: "Nevada & Regional Resources",
    icon: MapPin,
    blurb: "State and local agencies serving Nevada communities.",
    links: [
      { name: "Nevada Department of Health & Human Services (DHHS)", url: "https://dhhs.nv.gov", desc: "State health and human services." },
      { name: "Nevada Division of Public & Behavioral Health (DPBH)", url: "https://dpbh.nv.gov", desc: "State public and behavioral health programs." },
      { name: "Nevada Office of Minority Health & Equity", url: "https://dhhs.nv.gov/Programs/OMH/Office_of_Minority_Health_and_Equity/", desc: "Advancing equity across Nevada populations." },
      { name: "Southern Nevada Health District (SNHD)", url: "https://www.snhd.info", desc: "Public health for the Las Vegas metro area." },
      { name: "Washoe County Health District", url: "https://www.washoecounty.gov/health/", desc: "Public health for the Reno/Sparks region." },
    ],
  },
  {
    id: "evaluation",
    title: "Evaluation Standards & Practice",
    icon: ClipboardCheck,
    blurb: "Frameworks and professional standards that guide rigorous evaluation.",
    links: [
      { name: "CDC Program Evaluation Framework", url: "https://www.cdc.gov/evaluation/php/about/index.html", desc: "Foundational framework for public health program evaluation." },
      { name: "American Evaluation Association (AEA)", url: "https://www.eval.org", desc: "Professional standards and evaluator resources." },
      { name: "CDC Community Health Assessment & Improvement", url: "https://www.cdc.gov/public-health-gateway/php/national-public-health-performance-standards/index.html", desc: "Standards for performance and community assessment." },
    ],
  },
];

export default function Resources() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="resources-page">
      <div className="mb-14 max-w-3xl">
        <div className="overline text-[var(--red)] mb-3">Resources</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
          Public &amp; Population Health Resources
        </h1>
        <p className="text-[var(--text-2)] mt-4 leading-relaxed">
          A curated directory of trusted agencies, datasets, funding portals, and evaluation standards we draw on in
          our work — and that partners, agencies, and communities can use to advance evidence-based, equitable public
          health. Links open in a new tab and are maintained by their respective organizations.
        </p>
      </div>

      <div className="space-y-16">
        {GROUPS.map((g) => (
          <section key={g.id} data-testid={`resources-${g.id}`}>
            <div className="flex items-start gap-3 mb-6">
              <g.icon className="w-6 h-6 text-[var(--red)] mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl font-semibold tracking-tight">{g.title}</h2>
                <p className="text-sm text-[var(--text-2)] mt-1">{g.blurb}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
              {g.links.map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[var(--bg)] p-6 group hover:bg-white/5 transition-colors flex flex-col"
                  data-testid={`resource-link-${g.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-medium text-white leading-snug group-hover:text-[var(--red)] transition-colors">
                      {l.name}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-[var(--text-3)] group-hover:text-[var(--red)] shrink-0 mt-1" />
                  </div>
                  <p className="text-sm text-[var(--text-2)] mt-2">{l.desc}</p>
                </a>
              ))}
              {g.links.length % 3 !== 0 && (
                <div
                  className="hidden lg:flex relative bg-[var(--bg)] overflow-hidden group"
                  data-testid={`resource-filler-${g.id}`}
                >
                  <img
                    src={HELPING_HAND_IMG}
                    alt="Communities supporting one another"
                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/50 to-transparent" />
                  <div className="relative z-10 p-6 flex flex-col justify-end">
                    <HeartHandshake className="w-5 h-5 text-[var(--red)] mb-2" />
                    <p className="text-sm text-white font-medium leading-snug">Stronger together.</p>
                    <p className="text-xs text-[var(--text-2)] mt-1">We partner with the communities we serve.</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 text-xs text-[var(--text-3)] border-t border-[var(--border)] pt-6">
        PHMEG is an independent firm and is not affiliated with or endorsed by the organizations listed above. External
        links are provided for informational purposes; we are not responsible for the content of third-party sites.
      </div>
    </div>
  );
}
